// src/shared-components/forms/add-board-content/add-article/published-article/CommentsPreview.jsx
import { useEffect, useState } from 'react';
import React from 'react';

import GalleryService from '@/services/gallery.service';
import LikeButton from '@/shared-components/buttons/components/LikeButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import '@/shared-components/forms/add-board-content/shared-components/board-comments/CommentList.css';
import './CommentsPreview.css';

export default function CommentsPreview({
  articleId,
  commentCount,
  onOpenSheet,
  onReplyPreview
}) {
  const [comments, setComments]       = useState([]);
  const [openReplies, setOpenReplies] = useState({});

  useEffect(() => {
    let mounted = true;

    GalleryService.getComments('post', articleId, { limit: 4, offset: 0 })
      .then(all => {
        if (!mounted) return;
        const parents = all
          .filter(c => c.comment_parent === null)
          .sort((a, b) => ((b.like_count||0)-(a.like_count||0)) || (new Date(b.createdAt)-new Date(a.createdAt)))
          .slice(0, 4);

        setComments(parents.map(c => ({ ...c, replies: [] })));

        parents.forEach(parent => {
          GalleryService.getCommentReplies('post', articleId, parent._id)
            .then(replyArray => {
              if (!mounted) return;
              const topReplies = (replyArray || [])
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 2);
              setComments(curr =>
                curr.map(c =>
                  c._id === parent._id
                    ? { ...c, replies: topReplies }
                    : c
                )
              );
            })
            .catch(console.error);
        });
      })
      .catch(console.error);

    return () => { mounted = false; };
  }, [articleId]);

  return (
    <div className="article-comments-preview">
      <ul className="comment-list">
        {comments.map(c => (
          <React.Fragment key={c._id}>
            {/* ——— Parent ——— */}
            <li className="artwork-view-thought__container element-non-selectable">
              <UserProfileImage
                className="artwork-view-thought__user-image"
                image={c.commenter.user_image?.image_id}
                typeOfProfile={c.commenter.user_type}
              />
              <div className="artwork-view-thought__text-section">
                <Text
                  className="artwork-view-thought__small-text username dots-on-overflow font-size-medium"
                  onClick={() => onReplyPreview(c)}>
                  {c.commenter.user_username}
                </Text>
                <Text className="artwork-view-thought__text">
                  {Utils.parserUserMentions(c.comment_text)}
                </Text>
                <div>
                  <Text className="artwork-view-thought__small-text elapsed font-size-small">
                    {Utils.elapsedTime(new Date(c.createdAt).getTime())}
                  </Text>
                  <Text
                    className="artwork-view-thought__small-text reply font-size-small"
                    onClick={() => onReplyPreview(c)}>
                    Reply
                  </Text>
                </div>

                {/* ——— NEW: view-more toggle just like CommentList ——— */}
                {c.reply_count > 0 && (
                  <Text
                    className="artwork-view-thought__small-text view-more font-size-small"
                    onClick={() =>
                      setOpenReplies(m => ({ ...m, [c._id]: !m[c._id] }))
                    }>
                    {openReplies[c._id]
                      ? `Hide replies (${c.reply_count})`
                      : `View replies (${c.reply_count})`}
                  </Text>
                )}

                <div className="artwork-view-thought__like-section">
                  <LikeButton
                    itemId={c._id}
                    typeOfItem="COMMENT"
                    isLiked={c.is_liked}
                    onLikeAction={async newStatus => {
                      if (newStatus) {
                        await GalleryService.likeComment(c._id);
                      } else {
                        await GalleryService.unlikeComment(c._id);
                      }
                      setComments(cs =>
                        cs.map(x =>
                          x._id === c._id
                            ? {
                              ...x,
                              is_liked: newStatus,
                              like_count: x.like_count + (newStatus ? 1 : -1)
                            }
                            : x
                        )
                      );
                    }}
                    className="artwork-view-thought__like"
                  />
                  <span className="artwork-view-thought__like-count">
                    {c.like_count > 0 &&
                      Utils.numberParserMillionThousand(c.like_count)}
                  </span>
                </div>
              </div>
            </li>

            {/* ——— Inline replies, only when toggled open ——— */}
            {openReplies[c._id] &&
              c.replies.map(r => (
                <li
                  key={r._id}
                  className="artwork-view-thought__container element-non-selectable reply"
                >
                  <UserProfileImage
                    className="artwork-view-thought__user-image"
                    image={r.commenter.user_image?.image_id}
                    typeOfProfile={r.commenter.user_type}
                  />
                  <div className="artwork-view-thought__text-section">
                    <Text
                      className="artwork-view-thought__small-text username dots-on-overflow font-size-medium"
                      onClick={() => onReplyPreview(r)}>
                      {r.commenter.user_username}
                    </Text>
                    <Text className="artwork-view-thought__text">
                      {Utils.parserUserMentions(r.comment_text)}
                    </Text>
                    <div>
                      <Text className="artwork-view-thought__small-text elapsed font-size-small">
                        {Utils.elapsedTime(new Date(r.createdAt).getTime())}
                      </Text>
                      <Text
                        className="artwork-view-thought__small-text reply font-size-small"
                        onClick={() => onReplyPreview(r)}>
                        Reply
                      </Text>
                    </div>
                    <div className="artwork-view-thought__like-section">
                      <LikeButton
                        itemId={r._id}
                        typeOfItem="COMMENT"
                        isLiked={r.is_liked}
                        className="artwork-view-thought__like"
                      />
                      <span className="artwork-view-thought__like-count">
                        {r.like_count > 0 &&
                          Utils.numberParserMillionThousand(r.like_count)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
          </React.Fragment>
        ))}
      </ul>
      {commentCount > 4 && (
        <button
          className="article-comments-preview__show-all"
          onClick={onOpenSheet}
        >
          Show all {commentCount} comments
        </button>
      )}
    </div>
  );
}
